import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { ArrowLeft, Smartphone, Globe, Cpu, Database, Shield, Zap, Code, Palette, LucideIcon, FileText, Image as ImageIcon, Link as LinkIcon, QrCode, Download, ExternalLink, Eye, Sparkles } from 'lucide-react';
import MobileLayout from '../layouts/MobileLayout';
import { serviceApiService, Service } from '../../services/serviceApi';
import { servicePageConfigApiService, ServicePageContent } from '../../services/servicePageConfigApi';

interface ServiceWithContent extends Service {
  page_contents?: ServicePageContent[];
}

const getServiceIcon = (serviceName: string): LucideIcon => {
  const name = serviceName.toLowerCase();
  if (name.includes('mobile') || name.includes('app')) return Smartphone;
  if (name.includes('web') || name.includes('site')) return Globe;
  if (name.includes('ia') || name.includes('intelligence') || name.includes('ai')) return Cpu;
  if (name.includes('base') || name.includes('data')) return Database;
  if (name.includes('sécurité') || name.includes('security')) return Shield;
  if (name.includes('optimisation') || name.includes('performance')) return Zap;
  if (name.includes('design') || name.includes('ui')) return Palette;
  return Code;
};

const MobileServicesPageNew: React.FC = () => {
  const navigate = useNavigate();
  const [services, setServices] = useState<ServiceWithContent[]>([]);
  const [loading, setLoading] = useState(true);
  const [selectedService, setSelectedService] = useState<ServiceWithContent | null>(null);

  useEffect(() => {
    loadServices();
  }, []);

  const loadServices = async () => {
    try {
      setLoading(true);
      
      // 1. Charger les services
      const servicesData = await serviceApiService.getServices(true);
      const servicesArray = Array.isArray(servicesData) ? servicesData : [];
      
      // 2. Pour chaque service, charger les contenus configurés par l'admin
      const servicesWithContents: ServiceWithContent[] = [];
      for (const service of servicesArray) {
        try {
          const contents = await servicePageConfigApiService.getServicePageContentsPublic(service.id);
          servicesWithContents.push({
            ...service,
            page_contents: contents
          });
        } catch (err) {
          console.error(`Erreur lors du chargement du contenu pour ${service.name}:`, err);
          servicesWithContents.push({
            ...service,
            page_contents: []
          });
        }
      }
      
      setServices(servicesWithContents);
    } catch (error) {
      console.error('Erreur lors du chargement des services:', error);
    } finally {
      setLoading(false);
    }
  };

  // Detail View
  if (selectedService) {
    const Icon = getServiceIcon(selectedService.name);
    const activeContents = selectedService.page_contents || [];
    const images = activeContents.filter(c => c.content_type === 'image' && c.content_data.image_url);
    const pdfs = activeContents.filter(c => c.content_type === 'pdf' && c.content_data.pdf_url);
    const pdfsWithCover = pdfs.filter(p => p.content_data.pdf_cover_url);
    const pdfsWithoutCover = pdfs.filter(p => !p.content_data.pdf_cover_url);
    const links = activeContents.filter(c => c.content_type === 'link' && c.content_data.link_url);
    const texts = activeContents.filter(c => c.content_type === 'text' && c.content_data.text);
    const qrCodes = activeContents.filter(c => c.content_type === 'qr_code');
    
    // Combiner images et PDFs avec couverture pour la galerie visuelle
    const galleryItems = [
      ...images.map(img => ({ ...img, displayType: 'image' as const })),
      ...pdfsWithCover.map(pdf => ({ ...pdf, displayType: 'pdf_cover' as const }))
    ];

    return (
      <MobileLayout>
        {/* Hero Header with gradient */}
        <div className="relative bg-gradient-to-br from-amber-500 via-amber-600 to-orange-600 px-4 py-10 overflow-hidden">
          {/* Decorative elements */}
          <div className="absolute top-0 right-0 w-32 h-32 bg-white/10 rounded-full blur-3xl" />
          <div className="absolute bottom-0 left-0 w-24 h-24 bg-white/10 rounded-full blur-2xl" />
          
          <button
            onClick={() => setSelectedService(null)}
            className="flex items-center gap-2 text-white/90 hover:text-white mb-6 relative z-10 transition-colors"
          >
            <ArrowLeft className="w-5 h-5" />
            <span className="text-sm font-medium">Tous les services</span>
          </button>
          
          <div className="relative z-10">
            <div className="w-20 h-20 bg-white/20 backdrop-blur-md rounded-3xl flex items-center justify-center mb-4 shadow-lg">
              <Icon className="w-10 h-10 text-white" />
            </div>
            <h1 className="text-3xl font-bold text-white mb-4 leading-tight">{selectedService.name}</h1>
            {selectedService.price_display && (
              <div className="inline-flex items-center gap-2 px-4 py-2 bg-white/20 backdrop-blur-md rounded-xl">
                <Sparkles className="w-4 h-4 text-white" />
                <span className="text-lg font-bold text-white">{selectedService.price_display}</span>
              </div>
            )}
          </div>
        </div>

        {/* Content Sections */}
        <div className="px-4 py-6 space-y-6">
          {/* Gallery - Images et PDF Covers */}
          {galleryItems.length > 0 && (
            <div>
              <h3 className="text-lg font-bold text-gray-900 mb-3 flex items-center gap-2">
                <ImageIcon className="w-5 h-5 text-amber-600" />
                Galerie ({galleryItems.length})
              </h3>
              <div className="grid grid-cols-2 gap-3">
                {galleryItems.map((item) => (
                  <a
                    key={item.id}
                    href={item.displayType === 'image' ? item.content_data.image_url : item.content_data.pdf_url}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="relative group rounded-2xl overflow-hidden aspect-square shadow-md bg-gray-50 flex items-center justify-center"
                  >
                    <img
                      src={item.displayType === 'image' ? item.content_data.image_url : item.content_data.pdf_cover_url}
                      alt="Service"
                      className="w-full h-full object-contain p-2"
                    />
                    {/* Badge PDF si c'est une couverture de PDF */}
                    {item.displayType === 'pdf_cover' && (
                      <div className="absolute top-2 left-2 px-2 py-1 bg-red-600 text-white text-xs font-bold rounded-lg shadow-md flex items-center gap-1">
                        <FileText className="w-3 h-3" />
                        PDF
                      </div>
                    )}
                    <div className="absolute inset-0 bg-gradient-to-t from-black/60 via-transparent to-transparent opacity-0 group-active:opacity-100 transition-opacity flex items-end justify-center pb-4">
                      <Eye className="w-6 h-6 text-white" />
                    </div>
                  </a>
                ))}
              </div>
            </div>
          )}

          {/* PDF Documents - Only PDFs without cover (PDFs with cover are in gallery) */}
          {pdfsWithoutCover.length > 0 && (
            <div>
              <h3 className="text-lg font-bold text-gray-900 mb-3 flex items-center gap-2">
                <FileText className="w-5 h-5 text-red-600" />
                Documents ({pdfsWithoutCover.length})
              </h3>
              <div className="space-y-3">
                {pdfsWithoutCover.map((content) => (
                  <a
                    key={content.id}
                    href={content.content_data.pdf_url}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="block bg-gradient-to-br from-red-50 to-orange-50 rounded-2xl p-4 border border-red-100 hover:border-red-300 transition-all shadow-sm active:scale-95"
                  >
                    <div className="flex items-center gap-4">
                      <div className="w-16 h-20 bg-gradient-to-br from-red-500 to-red-600 rounded-xl flex flex-col items-center justify-center text-white shadow-lg flex-shrink-0">
                        <FileText className="w-8 h-8 mb-1" />
                        <span className="text-xs font-bold">PDF</span>
                      </div>
                      <div className="flex-1 min-w-0">
                        <div className="font-semibold text-gray-900 mb-1 text-base">{content.content_data.pdf_title || 'Document PDF'}</div>
                        {content.content_data.pdf_description && (
                          <p className="text-xs text-gray-600 line-clamp-2">{content.content_data.pdf_description}</p>
                        )}
                        <div className="flex items-center gap-1 text-red-600 font-medium text-xs mt-2">
                          <Download className="w-3 h-3" />
                          <span>Télécharger</span>
                        </div>
                      </div>
                    </div>
                  </a>
                ))}
              </div>
            </div>
          )}

          {/* Links */}
          {links.length > 0 && (
            <div>
              <h3 className="text-lg font-bold text-gray-900 mb-3 flex items-center gap-2">
                <LinkIcon className="w-5 h-5 text-blue-600" />
                Liens utiles ({links.length})
              </h3>
              <div className="space-y-2">
                {links.map((content) => (
                  <a
                    key={content.id}
                    href={content.content_data.link_url}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="flex items-center gap-3 p-4 bg-white rounded-2xl border border-gray-200 hover:border-blue-400 hover:shadow-md transition-all active:scale-95"
                  >
                    <div className="w-12 h-12 bg-gradient-to-br from-blue-500 to-blue-600 rounded-xl flex items-center justify-center shadow-md flex-shrink-0">
                      <LinkIcon className="w-6 h-6 text-white" />
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className="font-semibold text-gray-900 text-sm truncate">{content.content_data.link_text || 'Lien externe'}</div>
                      <div className="text-xs text-gray-500 truncate">{content.content_data.link_url}</div>
                    </div>
                    <ExternalLink className="w-5 h-5 text-gray-400 flex-shrink-0" />
                  </a>
                ))}
              </div>
            </div>
          )}

          {/* QR Codes */}
          {qrCodes.length > 0 && (
            <div>
              <h3 className="text-lg font-bold text-gray-900 mb-3 flex items-center gap-2">
                <QrCode className="w-5 h-5 text-purple-600" />
                QR Codes ({qrCodes.length})
              </h3>
              <div className="grid grid-cols-2 gap-3">
                {qrCodes.map((content) => (
                  <div key={content.id} className="bg-white rounded-2xl p-4 border border-gray-200 text-center shadow-sm">
                    {content.content_data.qr_mode === 'upload' && content.content_data.qr_image_url ? (
                      <img
                        src={content.content_data.qr_image_url}
                        alt="QR Code"
                        className="w-full aspect-square object-contain rounded-xl mb-2"
                      />
                    ) : (
                      <div className="w-full aspect-square bg-gray-50 rounded-xl flex items-center justify-center mb-2">
                        <QrCode className="w-16 h-16 text-gray-300" />
                      </div>
                    )}
                    <div className="text-xs font-medium text-gray-700">{content.content_data.qr_label || 'QR Code'}</div>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Text Information */}
          {texts.length > 0 && (
            <div>
              <h3 className="text-lg font-bold text-gray-900 mb-3 flex items-center gap-2">
                <FileText className="w-5 h-5 text-gray-600" />
                Informations
              </h3>
              <div className="space-y-3">
                {texts.map((content) => (
                  <div key={content.id} className="bg-white rounded-2xl p-4 border border-gray-200 shadow-sm">
                    <p className="text-sm text-gray-700 leading-relaxed whitespace-pre-wrap">{content.content_data.text}</p>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* No Content */}
          {activeContents.length === 0 && (
            <div className="text-center py-12">
              <div className="w-16 h-16 bg-gray-100 rounded-full flex items-center justify-center mx-auto mb-4">
                <FileText className="w-8 h-8 text-gray-400" />
              </div>
              <p className="text-gray-500 text-sm">Aucune ressource disponible pour ce service</p>
            </div>
          )}
        </div>

        {/* CTA Footer */}
        <div className="px-4 pb-6">
          <div className="bg-gradient-to-br from-amber-500 to-amber-600 rounded-2xl p-6 text-center shadow-xl">
            <h2 className="text-xl font-bold text-white mb-2">
              Intéressé par ce service ?
            </h2>
            <p className="text-sm text-white/90 mb-4">
              Contactez-nous pour un devis personnalisé
            </p>
            <button
              onClick={() => navigate('/contact')}
              className="w-full py-3 bg-white text-amber-600 rounded-xl font-bold shadow-lg hover:shadow-xl transition-all active:scale-95"
            >
              Demander un devis
            </button>
          </div>
        </div>
      </MobileLayout>
    );
  }

  // List View
  return (
    <MobileLayout>
      {/* Header */}
      <div className="bg-gradient-to-br from-amber-500 via-amber-600 to-orange-600 px-4 py-8 relative overflow-hidden">
        <div className="absolute top-0 right-0 w-40 h-40 bg-white/10 rounded-full blur-3xl" />
        <button
          onClick={() => navigate('/')}
          className="flex items-center gap-2 text-white/90 hover:text-white mb-4 relative z-10"
        >
          <ArrowLeft className="w-5 h-5" />
          <span className="text-sm font-medium">Retour</span>
        </button>
        <div className="relative z-10">
          <h1 className="text-3xl font-bold text-white mb-2">Nos Services</h1>
          <p className="text-sm text-white/90">
            Solutions digitales complètes pour votre entreprise
          </p>
        </div>
      </div>

      {/* Services Grid */}
      <section className="px-4 py-6">
        {loading ? (
          <div className="text-center py-12">
            <div className="animate-spin rounded-full h-12 w-12 border-4 border-amber-600 border-t-transparent mx-auto mb-4"></div>
            <p className="text-gray-600 text-sm">Chargement des services...</p>
          </div>
        ) : services.length === 0 ? (
          <div className="text-center py-12">
            <p className="text-gray-600">Aucun service disponible pour le moment.</p>
          </div>
        ) : (
          <div className="grid grid-cols-1 gap-4">
            {services.map((service) => {
              const Icon = getServiceIcon(service.name);
              const activeContents = service.page_contents || [];
              const imageCount = activeContents.filter(c => c.content_type === 'image').length;
              const pdfCount = activeContents.filter(c => c.content_type === 'pdf').length;
              const linkCount = activeContents.filter(c => c.content_type === 'link').length;
              const firstImage = activeContents.find(c => c.content_type === 'image' && c.content_data.image_url);
              const firstPdfWithCover = activeContents.find(c => c.content_type === 'pdf' && c.content_data.pdf_cover_url);
              const pdfs = activeContents.filter(c => c.content_type === 'pdf' && c.content_data.pdf_url);
              const links = activeContents.filter(c => c.content_type === 'link' && c.content_data.link_url);
              
              // Prioriser l'image, sinon la couverture PDF si disponible
              const coverMedia = firstImage || firstPdfWithCover;

              return (
                <div
                  key={service.id}
                  onClick={() => setSelectedService(service)}
                  className="bg-white rounded-2xl shadow-md border border-gray-100 overflow-hidden active:scale-95 transition-all cursor-pointer"
                >
                  {/* Service Image, PDF Cover or Icon */}
                  {coverMedia ? (
                    <div className="relative h-40 bg-gradient-to-br from-gray-100 to-gray-50 flex items-center justify-center">
                      <img
                        src={coverMedia.content_type === 'image' ? coverMedia.content_data.image_url : coverMedia.content_data.pdf_cover_url}
                        alt={service.name}
                        className="w-full h-full object-contain p-2"
                      />
                      {/* Badge indiquant le type */}
                      <div className="absolute top-3 right-3 w-12 h-12 bg-white/90 backdrop-blur-sm rounded-xl flex items-center justify-center shadow-lg">
                        {coverMedia.content_type === 'pdf' ? (
                          <FileText className="w-6 h-6 text-red-600" />
                        ) : (
                          <Icon className="w-6 h-6 text-amber-600" />
                        )}
                      </div>
                      {/* Label PDF si c'est une couverture PDF */}
                      {coverMedia.content_type === 'pdf' && (
                        <div className="absolute top-3 left-3 px-2 py-1 bg-red-600 text-white text-xs font-bold rounded-lg shadow-md">
                          PDF
                        </div>
                      )}
                    </div>
                  ) : (
                    <div className="h-32 bg-gradient-to-br from-amber-100 via-amber-50 to-orange-50 flex items-center justify-center relative overflow-hidden">
                      <div className="absolute top-0 right-0 w-24 h-24 bg-amber-200/20 rounded-full blur-2xl" />
                      <Icon className="w-16 h-16 text-amber-600 relative z-10" />
                    </div>
                  )}

                  {/* Service Content */}
                  <div className="p-4">
                    <h3 className="text-lg font-bold text-gray-900 mb-2">{service.name}</h3>
                    <p className="text-xs text-gray-600 line-clamp-2 mb-3 leading-relaxed">{service.description}</p>
                    
                    {/* Price */}
                    {service.price_display && (
                      <div className="inline-block px-3 py-1.5 bg-amber-50 rounded-lg mb-3">
                        <span className="text-sm font-bold text-amber-700">{service.price_display}</span>
                      </div>
                    )}

                    {/* Resources Preview */}
                    {activeContents.length > 0 && (
                      <div className="space-y-2 pt-3 border-t border-gray-100">
                        {/* PDF Preview */}
                        {pdfs.length > 0 && (
                          <div className="space-y-1.5">
                            <div className="flex items-center gap-1.5 text-xs font-semibold text-red-600">
                              <FileText className="w-3.5 h-3.5" />
                              <span>Documents ({pdfs.length})</span>
                            </div>
                            {pdfs.slice(0, 2).map((pdf) => (
                              <div key={pdf.id} className="text-xs text-gray-600 pl-5 truncate">
                                • {pdf.content_data.pdf_title || 'Document PDF'}
                              </div>
                            ))}
                            {pdfs.length > 2 && (
                              <div className="text-xs text-gray-400 pl-5">+{pdfs.length - 2} autre{pdfs.length - 2 > 1 ? 's' : ''}</div>
                            )}
                          </div>
                        )}
                        
                        {/* Links Preview */}
                        {links.length > 0 && (
                          <div className="space-y-1.5">
                            <div className="flex items-center gap-1.5 text-xs font-semibold text-blue-600">
                              <LinkIcon className="w-3.5 h-3.5" />
                              <span>Liens ({links.length})</span>
                            </div>
                            {links.slice(0, 2).map((link) => (
                              <div key={link.id} className="text-xs text-gray-600 pl-5 truncate">
                                • {link.content_data.link_text || 'Lien externe'}
                              </div>
                            ))}
                            {links.length > 2 && (
                              <div className="text-xs text-gray-400 pl-5">+{links.length - 2} autre{links.length - 2 > 1 ? 's' : ''}</div>
                            )}
                          </div>
                        )}
                        
                        {/* Images Count */}
                        {imageCount > 0 && (
                          <div className="flex items-center gap-1.5 text-xs text-gray-500">
                            <ImageIcon className="w-3.5 h-3.5" />
                            <span>{imageCount} image{imageCount > 1 ? 's' : ''}</span>
                          </div>
                        )}
                      </div>
                    )}
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </section>

      {/* CTA */}
      <section className="px-4 py-6 bg-gray-50">
        <div className="bg-gradient-to-br from-amber-500 to-amber-600 rounded-2xl p-6 text-center shadow-xl">
          <h2 className="text-xl font-bold text-white mb-2">
            Besoin d'un devis ?
          </h2>
          <p className="text-sm text-white/90 mb-4">
            Contactez-nous pour discuter de votre projet
          </p>
          <button
            onClick={() => navigate('/contact')}
            className="w-full py-3 bg-white text-amber-600 rounded-xl font-bold shadow-lg"
          >
            Nous contacter
          </button>
        </div>
      </section>
    </MobileLayout>
  );
};

export default MobileServicesPageNew;
